import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildDeck, deckStep } from '../../domain/plants/plantDeck';

/**
 * Browsing a plant's category as a deck (SPEC-025 D-1, D-3, D-12).
 *
 * The screen composes a plant page; this is how one is left for the next, and it
 * is a use case rather than a component's business: which plant a swipe deals,
 * whether the card has to leave the screen first, and what that does to history.
 *
 * `deck` is recomputed from the URL every render and carries no state — the walk
 * this replaced needed a deque in the history entry because a path has a memory,
 * and a deck is a ring the URL already indexes.
 */

/**
 * How long the card in front takes to leave the screen (SPEC-025 D-12).
 *
 * Declared here rather than in the stylesheet because BOTH need it: the CSS runs
 * the animation and this module has to wait for it before navigating. One number,
 * handed to the stylesheet as a custom property, instead of two that drift.
 *
 * Long enough to watch. The card tips away before it slides — at 240ms the fold
 * was over before the eye found it, and what the reader saw was a card that had
 * simply gone.
 */
const DEAL_MS = 320;

/** Does this reader want the card dealt, or just swapped? */
function prefersReducedMotion() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function usePlantDeck({ plants, plantId, localeCode, languageCode }) {
    const navigate = useNavigate();
    // Which way the card in front is leaving, while it is leaving.
    const [dealing, setDealing] = useState(null);
    const dealingRef = useRef(null);
    const deck = buildDeck(plants, plantId, { languageCode });

    // A backstop for a plant that arrives by some other route while a card is
    // mid-deal — a companion tap, a deck control. The deal's own handover clears
    // this in the same commit as the navigation, which is what keeps the arriving
    // card from being drawn at the departing one's position for a frame.
    useEffect(() => setDealing(null), [plantId]);
    // A card half-dealt when the screen goes has a timer pointing at a navigate
    // that must not happen.
    useEffect(() => () => window.clearTimeout(dealingRef.current), []);

    const step = useCallback((direction) => {
        const next = deckStep(deck, direction);

        // Nowhere to go happens only in a category of one, where every step
        // would land back on the card already showing (D-2). Everywhere else the
        // deck is a ring and a swipe always deals something.
        //
        // And nothing while a card is already on its way out: a second swipe
        // mid-deal would navigate twice and skip a plant.
        if (!next || dealingRef.current) {
            return;
        }

        // A REPLACE, where the walk pushed (D-3). A deck is one place and the URL
        // is a bookmark of where the reader is inside it, so one back leaves for
        // the shelf with its remembered scroll rather than unwinding five swipes.
        const arrive = () => navigate(`/${localeCode}/plants/${next.id}`, { replace: true });

        if (prefersReducedMotion()) {
            arrive();

            return;
        }

        // The card leaves BEFORE the plant changes (D-12). The alternative —
        // navigate now and animate the arrival — swaps the card's contents while
        // it is still on screen, so the plant being dealt away is the wrong one
        // for the last frames of its own exit.
        dealingRef.current = window.setTimeout(() => {
            dealingRef.current = null;
            // Cleared in the SAME batch as the navigation, not after it. React
            // groups these into one commit, so the new plant and the card's
            // resting position land together.
            //
            // Left to an effect on `plantId`, the clearing happened a render
            // LATER — which committed one frame with the arriving plant drawn at
            // the departing card's position, off the side of the screen at 126%
            // and 30 degrees, before snapping back. That frame was the flicker
            // (D-26).
            setDealing(null);
            arrive();
        }, DEAL_MS);
        setDealing(direction);
    }, [deck, localeCode, navigate]);

    return { deal: DEAL_MS, deck, dealing, step };
}
